import React from 'react';
import PropTypes from 'prop-types';
import fetch from '../../../../utils/fetch.js';
import { required } from '../../../../utils/formFieldValidators';
import { DEFAULT_COUNTRY_CODE, IDEAL_POSTCODES_API_KEY } from '../../../../utils/constants';
import { SelectField, SelectFieldAsync } from '../../../01_atoms/FormElements';

import styles from './index.module.scss';
import formattedTextStyles from '../../../01_atoms/FormattedText/index.module.scss';

const debug = require('debug')('cw:AddressAutofillFormUK.js');

const NoOptionsMessage = ({ changeAddressSchemaType, text }) => (
  <div className="helper-text">
    {text} please&nbsp;
    <span
      className="link-button"
      onClick={() => changeAddressSchemaType('manually', false)}
      onKeyPress={() => changeAddressSchemaType('manually', false)}
    >
      enter address manually
    </span>
  </div>
);

NoOptionsMessage.propTypes = {
  changeAddressSchemaType: PropTypes.func.isRequired,
  text: PropTypes.string.isRequired,
};

/**
 * A stateful component that manages a UK address format and provides APIs to autocomplete
 * postcodes and autofill addresses.
 *
 * See https://getaddress.io/Documentation for address lookup API results docs.
 */
class AddressAutofillFormUK extends React.Component {
  // The reference of a timeout that makes sure the postcode autocomplete lookup isn't called on
  // every keystroke.
  postcodeDebouncer = null;

  // Minimum number of characters required for the postcode API lookup.
  MIN_POSTCODE_CHAR_LOOKUP = 2;

  // Minimum number of characters in a valid UK postcode.
  // See https://en.wikipedia.org/wiki/Postcodes_in_the_United_Kingdom
  MIN_VALID_POSTCODE_LENGTH = 4;

  constructor(props) {
    super(props);

    this.state = {
      // The current full valid postcode.
      postcode: props.postalCode ?? '',
      // The list of all possible postcodes as select options,
      // based on an incomplete postcode input.
      postcodeOptions: [],
      // All addresses of the current full valid postcode.
      addresses: [],
      isLoading: false,
    };
  }

  onPostCodeChange = (value) => {
    clearTimeout(this.postcodeDebouncer);

    let callback = Promise.resolve();

    if (value.length >= this.MIN_POSTCODE_CHAR_LOOKUP) {
      callback = new Promise((resolve, reject) => {
        this.postcodeDebouncer = setTimeout(() => {
          fetch(`https://api.postcodes.io/postcodes/${value}/autocomplete`, {
            timeout: 12000,
          })
            .then((resp) => {
              if (resp.body.status === 200) {
                if (resp.body.result && resp.body.result.length) {
                  const postcodeOptions = resp.body.result.map((postcode) => ({
                    value: postcode,
                    label: postcode,
                  }));

                  this.setState({ postcodeOptions });
                  resolve(postcodeOptions);
                } else {
                  // If there are no results, resp.body.result is null.
                  resolve([]);
                }
              } else {
                // The API might respond with an error code.
                debug(resp);
                reject(resp);
              }
            })
            .catch((error) => {
              // Might be because API is down or region-blocked. Options can still be added.
              debug(error);
              reject(error);
            });
        }, 400);
      });
    }
    return callback;
  };

  onPostcodeSelect = (option) => {
    // If the option was cleared, it will be null.
    if (!option) {
      this.filterPostcodes({ clearedPostcode: true });
      return;
    }

    const postcode = option.value;

    const { setAddressValues } = this.props;

    this.setState({ postcode, isLoading: true }, () => {
      setAddressValues({
        addressLine1: '',
        addressLine2: '',
        locality: '',
        administrativeArea: {},
      });

      // See https://docs.ideal-postcodes.co.uk/docs/migrate/getaddressio/postcode-lookup
      // and https://docs.ideal-postcodes.co.uk/docs/postcode-lookup
      fetch(`https://api.ideal-postcodes.co.uk/v1/postcodes/${postcode}`, {
        query: {
          api_key: IDEAL_POSTCODES_API_KEY,
        },
        timeout: 12000,
      })
        .then((resp) => {
          this.filterPostcodes({ gaResponse: resp });
        })
        .catch((error) => {
          this.filterPostcodes({});
          // Silent error. Happens when API limit is exceeded, or when an invalid
          // postcode is submitted, or when there is any higher-level API error
          // (unavailablity or blocked).
          debug(error);
        });
    });
  };

  filterPostcodes = ({ gaResponse, clearedPostcode }) => {
    if (clearedPostcode) {
      const { setAddressValues } = this.props;
      // If the option was cleared, delete all addresses and set postcode to non-null to avoid
      // removing the address fields.
      this.setState(
        { postcode: '', addresses: [], isLoading: false },
        setAddressValues({
          addressLine1: '',
          addressLine2: '',
          locality: '',
          administrativeArea: {},
        }),
      );
      return;
    }

    if (!gaResponse) {
      this.setState({ isLoading: false });
      // TODO: show error.
      return;
    }

    const addresses = gaResponse.body?.result ?? [];
    this.setState({ addresses, isLoading: false });
  };

  onAddressSelect = (option) => {
    // If the user cleared the field.
    if (!option) {
      return;
    }

    const { changeSchemaType } = this.props;

    // This happens only if the user puts in a custom address in line 1.
    if (option.value === option.label) {
      this.setState({}, changeSchemaType('manually', true));
      return;
    }

    const { addresses, postcode } = this.state;
    const { setAddressValues } = this.props;
    const selectedAddress = addresses[option.value];

    let addressLine1 = selectedAddress.line_1;
    addressLine1 += selectedAddress.line_2 ? `, ${selectedAddress.line_2}` : '';
    let addressLine2 = selectedAddress.line_3;
    addressLine2 +=
      addressLine2 && selectedAddress.dependant_locality
        ? `, ${selectedAddress.dependant_locality}`
        : selectedAddress.dependant_locality;
    setAddressValues({
      postalCode: postcode,
      addressLine1,
      addressLine2,
      locality: selectedAddress.post_town,
      administrativeArea: {
        GB: selectedAddress.county,
      },
    });

    changeSchemaType('manually', true);
  };

  /**
   * Text to display when there are no options, to help users type in postcode.
   */
  noPostcodeMessage = ({ inputValue }) => {
    const { changeSchemaType } = this.props;

    if (inputValue.length < this.MIN_POSTCODE_CHAR_LOOKUP) {
      return 'Type at least 2 characters';
    }
    return (
      <NoOptionsMessage
        changeAddressSchemaType={changeSchemaType}
        text="Sorry we cannot find this postcode,"
      />
    );
  };

  noAddressesMessage = () => {
    const { changeSchemaType } = this.props;
    return (
      <NoOptionsMessage
        changeAddressSchemaType={changeSchemaType}
        text="Is your address not listed?"
      />
    );
  };

  render() {
    const { postcode, postcodeOptions, addresses, isLoading } = this.state;
    const { changeSchemaType } = this.props;

    const addressOptions = [];
    addresses.forEach((a, i) => {
      addressOptions.push({
        value: `${i}`,
        label: `${a.line_1}${a.line_2 ? ', ' : ''}${a.line_2}`,
      });
    });

    return (
      <div id="address-autofill-uk" className={styles['address-autofill-uk']}>
        <div className={styles['address-autofill-uk__postcode-container']}>
          <div className={styles['address-autofill-uk__postcode-item']}>
            <SelectFieldAsync
              key="address.postalCode"
              name="address.postalCode"
              label="Postcode"
              placeholder="SA0 000"
              validate={required()}
              isRequired
              // Determines what type of message is shown when there are no options to select.
              noOptionsMessage={this.noPostcodeMessage}
              // Called as user types in postcode
              loadOptions={this.onPostCodeChange}
              // Called when an option is selected from the auto-completed postcode options.
              onOptionSelect={this.onPostcodeSelect}
              options={postcodeOptions}
              // TODO: Enable autofocus. We disable it because we see validation
              // error in case of UK by default.
              autoFocus={DEFAULT_COUNTRY_CODE === 'IE'}
              autoFill={false}
            />
          </div>

          <div className={`${styles['helper-text']} ${formattedTextStyles['formatted-text']}`}>
            or&nbsp;
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                changeSchemaType('manually', false);
              }}
              data-component="uk-manually-enter-address"
            >
              enter address manually
            </a>
          </div>
        </div>

        {postcode && (
          <SelectField
            key="address.addressLine1"
            name="address.addressLine1"
            label="Address"
            placeholder="Select your address"
            isClearable
            validate={required()}
            isRequired
            options={addressOptions}
            onOptionSelect={this.onAddressSelect}
            noOptionsMessage={this.noAddressesMessage}
            autoFocus
            defaultMenuIsOpen
            autoFill={false}
            isLoading={isLoading}
            styles={{ menuList: (base) => ({ ...base, maxHeight: 200 }) }}
          />
        )}
      </div>
    );
  }
}

AddressAutofillFormUK.propTypes = {
  postalCode: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  changeSchemaType: PropTypes.func.isRequired,
  setAddressValues: PropTypes.func.isRequired,
};

export default AddressAutofillFormUK;
